import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { PAGE_HEADER_HEIGHT } from "@/shared/ui/page-header";

interface RightRailContainerProps {
  /** Accessible label for the complementary landmark, e.g. "Selected notification detail". */
  ariaLabel: string;
  /** Rail header content, typically a "back" action. */
  header: ReactNode;
  /** Scrollable rail body content. */
  children: ReactNode;
  /** Optional content pinned to the bottom of the rail, e.g. a primary CTA. */
  footer?: ReactNode;
  /**
   * Renders the rail as a fixed overlay pinned to the viewport's right edge instead of a flex
   * sibling, so it floats above the page content without affecting its layout size. Used by the
   * shared layout-level notification center rail, which can be toggled without shifting the page.
   */
  floating?: boolean;
}

/**
 * Shared frame for route-level right-side detail rails (Notifications, and later Live view).
 * Owns the fixed width, border, sticky positioning, scrollable body, and pinned footer slot.
 * Page-specific detail content (summary, logbook, CTA copy) stays owned by the feature/page.
 */
export function RightRailContainer({
  ariaLabel,
  header,
  children,
  footer,
  floating = false,
}: RightRailContainerProps) {
  return (
    <Box
      component="aside"
      aria-label={ariaLabel}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        width: { xs: "100%", md: 360 },
        height: `calc(100vh - ${PAGE_HEADER_HEIGHT}px)`,
        // Floating mode overlays page content, so it needs an opaque fill to stay readable;
        // the non-floating (in-flow) rails sit over the console's own dark background already.
        backgroundColor: floating
          ? theme.palette.background.default
          : alpha(theme.palette.common.black, 0.22),
        borderLeft: { md: `1px solid ${alpha(theme.palette.common.white, 0.08)}` },
        ...(floating
          ? {
              position: "fixed",
              top: PAGE_HEADER_HEIGHT,
              right: 0,
              // Floating notification rails must remain above playback scrubbers and chart tooltips.
              zIndex: theme.zIndex.tooltip + 2,
              boxShadow: theme.shadows[8],
            }
          : {
              position: "sticky",
              top: 0,
              // Console main content has its own px:3/py:2.5 padding; cancel it here so the
              // rail sits flush against the right edge and spans full height, edge to edge.
              mt: { md: -2.5 },
              mb: { md: -2.5 },
              mr: { md: -3 },
            }),
      })}
    >
      <Box sx={{ flexShrink: 0, px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.5 }}>{header}</Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: { xs: 2, md: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </Box>
      {footer != null && (
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            px: { xs: 2, md: 2.5 },
            pt: 2,
            pb: 2,
            mt: 1,
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          })}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}
